import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import { BRAND_NAME } from '../branding';
import AccountModal from './AccountModal';
import RoleSwitcher from './RoleSwitcher';

/**
 * Shared Header / Navbar
 *
 * Kept intentionally minimal. The brand is on the left, the current user's
 * name shows as muted text next to it (a role switcher for a person with
 * several roles, plus the ⚙ My account button). The right side is a
 * row of text buttons (outline by default, primary for true CTAs) and the
 * sign-out action. No role-coloured badges, no "Welcome" decoration.
 *
 * Usage:
 * <Header
 *   user={user}
 *   onLogout={handleLogout}
 *   showDashboard={true}
 *   extraButtons={[
 *     { label: 'Customers', onClick: () => navigate('/customers') },
 *     { label: 'Capture Snapshot', onClick: handleCapture, variant: 'primary' }
 *   ]}
 * />
 */
const Header = ({
  user,
  onLogout,
  showDashboard = true,
  extraButtons = [],
  brandText = BRAND_NAME
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);

  const hasSeveralRoles = Array.isArray(user?.roles) && user.roles.length > 1;
  const isOnDashboard = location.pathname === '/dashboard' || location.pathname === '/';

  return (
    <nav className="app-nav">
      <div className="app-nav-content">
        <div className="app-nav-left">
          <h2 className="app-nav-brand">{brandText}</h2>
          {/* User identities (2026-10-30): one role = the plain name; several roles =
              the role switcher ("ESales · Sales ▾"). My account is the cog. */}
          {user && (hasSeveralRoles
            ? <RoleSwitcher user={user} />
            : <span className="app-nav-user">{user.username}</span>)}
          {user && (
            <button
              type="button"
              className="app-nav-cog"
              onClick={() => setAccountOpen(true)}
              aria-label="My account"
              title="My account — password, sign-in name, two-factor, passkeys, device alerts"
            >
              ⚙
            </button>
          )}
          {/* Auditor (2026-10-24): a standing reminder that this account only views. */}
          {user?.role === 'auditor' && (
            <span className="app-nav-role-chip" title="This account can view everything but cannot change anything">
              Auditor · read-only
            </span>
          )}
        </div>

        <div className="app-nav-actions">
          {showDashboard && !isOnDashboard && (
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="btn btn-outline btn-small"
            >
              Dashboard
            </button>
          )}

          {extraButtons.map((button, index) => {
            const variant = button.variant || 'outline';
            return (
              <button
                key={index}
                type="button"
                onClick={button.onClick}
                className={`btn btn-${variant} btn-small`}
                disabled={button.disabled}
              >
                {button.label}
              </button>
            );
          })}

          <button
            type="button"
            onClick={onLogout}
            className="btn btn-outline btn-small app-nav-signout"
          >
            Sign out
          </button>
        </div>
      </div>
      <AccountModal isOpen={accountOpen} onClose={() => setAccountOpen(false)} user={user} />
    </nav>
  );
};

export default Header;
